<template>
  <div class="sp-expandable-area" :style="style">
    <template v-for="section in sections" :key="section.key">
      <div v-if="section.isActivator" class="sp-expandable-area__header" :role="headerRole" :aria-level="headingLevel">
        <sp-button
          ref="activator"
          block
          variant="appearance-link"
          class="sp-expandable-area__activator"
          :aria-expanded="isExpanded"
          @click="toggle"
        >
          <slot name="title">
            {{ activatorLabel }}
          </slot>
          <sp-caret slot="append" class="sp-expandable-area__caret" :active="isExpanded"></sp-caret>
        </sp-button>
      </div>
      <div v-else id="content" ref="region" class="sp-expandable-area__content">
        <slot v-if="isExpanded" />
        <slot v-else name="collapsed" />
      </div>
    </template>
  </div>
</template>

<script setup>
/**
 * The SpExpandableArea component is used to display a section that can be expanded or collapsed.
 *
 * @displayName ExpandableArea
 * @group Custom Elements
 * @component sp-expandable-area
 */
import { computed, nextTick, onScopeDispose, ref, useAttrs, useTemplateRef, watch } from "vue";
import { usePreferredReducedMotion } from "@vueuse/core";
import { useAriaControls } from "#ce/composables/useAriaControls";
import { toBoolean } from "#ce/utils/props";
import { props as expandableAreaProps } from "./props.js";

const emit = defineEmits(["update-expanded"]);

defineOptions({ inheritAttrs: false });

const props = defineProps(expandableAreaProps);
const attrs = useAttrs();

const sections = computed(() => {
  const activator = { key: "activator", isActivator: true };
  const content = { key: "content", isActivator: false };

  return props.activatorPosition === "end" ? [content, activator] : [activator, content];
});

const activator = useTemplateRef("activator");
const region = useTemplateRef("region");
useAriaControls(
  () => activator.value?.[0],
  () => region.value?.[0] ?? null,
);

const isExpanded = ref(toBoolean(props.expanded));
const reducedMotion = usePreferredReducedMotion();
let heightAnimation;
let animationRevision = 0;

function emitExpanded(value) {
  emit("update-expanded", value);
}

function syncExpanded(value) {
  isExpanded.value = toBoolean(value);
}

function cancelHeightAnimation() {
  animationRevision += 1;
  heightAnimation?.cancel();
  heightAnimation = undefined;
}

async function animateHeight() {
  const element = region.value?.[0];
  if (!element || !toBoolean(props.animated) || reducedMotion.value === "reduce") {
    cancelHeightAnimation();

    return;
  }

  const fromHeight = element.getBoundingClientRect().height;
  cancelHeightAnimation();

  const revision = animationRevision;
  await nextTick();

  if (revision !== animationRevision) {
    return;
  }

  const toHeight = element.getBoundingClientRect().height;
  const styles = getComputedStyle(element);
  const duration = parseFloat(styles.transitionDuration) * 1000;
  const easing = styles.transitionTimingFunction;

  if (!Number.isFinite(duration) || !easing) {
    return;
  }

  heightAnimation = element.animate(
    [
      { height: `${fromHeight}px`, overflow: "hidden" },
      { height: `${toHeight}px`, overflow: "hidden" },
    ],
    { duration, easing },
  );
}

watch(isExpanded, emitExpanded);
watch(() => props.expanded, syncExpanded);
watch(isExpanded, animateHeight, { flush: "sync" });
watch(reducedMotion, cancelHeightAnimation);
watch(() => props.animated, cancelHeightAnimation);
onScopeDispose(cancelHeightAnimation);

function toggle() {
  isExpanded.value = !isExpanded.value;
}

const activatorLabel = computed(() => {
  if (isExpanded.value && props.expandedTitle) {
    return props.expandedTitle;
  }

  return attrs.title ?? "Show more";
});

const headerRole = computed(() => (props.headingLevel ? "heading" : undefined));

const style = computed(() => ({
  "--color": props.color,
}));
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
@layer expandable-area-font-defaults {
  .sp-expandable-area__activator {
    font: inherit;
    font-weight: var(--sp-comp-expandable-area-activator-font-weight, 500);
    font-size: var(--sp-comp-expandable-area-activator-font-size, 0.875rem);
  }
}

.sp-expandable-area {
  --color: var(--sp-comp-expandable-area-activator-color, inherit);
  --hover-color: var(--sp-comp-expandable-area-activator-hover-color, inherit);
  --caret-size: var(--sp-comp-expandable-area-caret-size, var(--sp-comp-caret-size, var(--sp-sys-icon-size-medium)));
  --expandable-area-caret-offset: var(--sp-comp-expandable-area-caret-offset, var(--sp-comp-caret-offset));
  --expandable-area-caret-active-nudge: var(
    --sp-comp-expandable-area-caret-active-nudge,
    var(--sp-comp-caret-active-nudge)
  );

  display: flex;
  flex-direction: column;
  width: var(--sp-comp-expandable-area-width, 100%);

  &__content {
    transition-property: none;
    transition-duration: var(--sp-ref-transition-expand-duration);
    transition-timing-function: var(--sp-ref-transition-expand-easing);
  }

  &__activator {
    --sp-comp-button-appearance-link-text-color: var(--color, currentColor);
    --sp-comp-button-appearance-link-hover-text-color: var(--hover-color);
    --sp-comp-button-appearance-link-text-decoration: var(--sp-comp-expandable-area-activator-text-decoration, none);
    --sp-comp-button-justify-content: var(--sp-comp-expandable-area-activator-justify-content, start);
    --sp-comp-button-gap: 0;

    color: var(--color);
    font: var(--sp-comp-expandable-area-activator-font-short, revert-layer);
    padding: var(--sp-comp-expandable-area-activator-padding, 0);
    margin-bottom: var(--sp-comp-expandable-area-activator-margin-bottom, 0);
    min-height: var(--sp-comp-expandable-area-activator-min-height, 0);
  }

  &__activator::part(base) {
    font: inherit;
  }

  &__activator::part(prepend) {
    display: none;
  }

  &__caret {
    --sp-comp-caret-size: var(--caret-size);
    --sp-comp-caret-offset: var(--expandable-area-caret-offset);
    --sp-comp-caret-active-nudge: var(--expandable-area-caret-active-nudge);

    display: flex;
    margin-inline-start: var(--sp-comp-expandable-area-activator-gap, 0.25ch);
  }
}
</style>
